/**
 * TenantContext - Fournit la configuration du tenant à tous les composants
 * Utilisé pour accéder aux paramètres hardware, UI, tax, security, etc.
 */
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { logger } from '../utils/logger';

// Types
export interface TenantConfig {
  hardware?: {
    scale?: {
      enabled: boolean;
      type: 'BAXTRAN_SL' | 'BLUETOOTH' | 'MANUAL';
      settings?: {
        baudRate?: number;
        port?: string;
        timeout?: number;
      };
    };
    printer?: {
      enabled: boolean;
      type: 'EPSON_TM_T20' | 'EPSON_TM_M30' | 'EPSON_TM_M267D' | 'EPSON_TM_T88' | 'BROWSER_PRINT';
      settings?: {
        ip?: string;
        port?: number;
        characterSet?: string;
        codePage?: number;
      };
      receiptOptions?: {
        showBarcode?: boolean;
        showQRCode?: boolean;
        footer?: string;
        copies?: number;
        openCashDrawer?: boolean;
      };
    };
    barcode_scanner?: {
      enabled: boolean;
      type: string;
    };
  };
  storeInfo?: {
    name?: string;
    address?: string;
    city?: string;
    postalCode?: string;
    phone?: string;
    taxNumber?: string;
    /** IBAN — imprimé sur les tickets payés par virement (réglages magasin). */
    iban?: string;
  };
  ui?: {
    device?: 'ipad_11' | 'ipad_pro_12_9' | 'desktop';
    orientation?: 'portrait' | 'landscape';
    language?: string;
    currency?: string;
    theme?: 'light' | 'dark';
    quick_login?: {
      enabled: boolean;
      require_pin: boolean;
    };
  };
  tax?: {
    default_rate?: number;
    rates?: {
      STANDARD_21?: number;
      REDUIT_12?: number;
      ALIM_6?: number;
      ZERO_0?: number;
    };
  };
  security?: {
    require_pin_for_voids?: boolean;
    require_pin_for_discounts?: boolean;
    max_discount_percent?: number;
  };
  features?: {
    consignes?: boolean;
    wallet?: boolean;
    loyalty?: boolean;
    inventory?: boolean;
    multi_vendor?: boolean;
  };
  ecommerce?: {
    enabled?: boolean;
    storeName?: string;
    storeDescription?: string;
    primaryColor?: string;
    secondaryColor?: string;
    accentColor?: string;
    logo?: string;
    deliveryEnabled?: boolean;
    pickupEnabled?: boolean;
    minOrderAmount?: number;
    freeDeliveryThreshold?: number;
    showVeganBadgeOnCards?: boolean;
    /** Assistance téléphonique pendant la préparation (Twilio). Fermée au lancement. */
    voiceAssistEnabled?: boolean;
    /** Lettre d'actualités par email : résumé hebdo à l'ouverture de la fenêtre de commande (défaut : activé). */
    newsletter?: { weeklyDigest?: boolean };
    /** Emails du magasin prévenus à chaque commande eshop payée (vide = contact). */
    orderNotificationEmails?: string[];
    /** Fenêtre hebdomadaire de prise de commande (0 = dimanche … 6 = samedi). */
    ordering?: {
      enabled?: boolean;
      open_weekday?: number;
      open_hour?: number;
      close_weekday?: number;
      close_hour?: number;
      closed_message?: string;
      /** Rayons commandables même hors fenêtre (épicerie). Les précommandes le sont toujours. */
      always_open_category_ids?: string[];
    };
    /** Retraits : jours d'ouverture magasin/marché, créneaux, délai. */
    pickup?: {
      days?: number[];
      slots?: string[];
      lead_days?: number;
      /** Jour même (délai 0) : heure belge limite ; null = toute la journée */
      same_day_until_hour?: number | null;
      max_days_ahead?: number;
    };
    /** Fermetures exceptionnelles (inventaire, congés), bornes incluses. */
    closures?: Array<{ from: string; to: string; reason?: string }>;
  };
  integrations?: {
    enfact?: {
      enabled?: boolean;
      apiKey?: string;
      companyUuid?: string;
      environment?: 'sandbox' | 'production';
      autoCreateInvoice?: boolean;
      peppolEnabled?: boolean;
    };
  };
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  active: boolean;
  config: TenantConfig;
  created_at?: string;
  updated_at?: string;
  // Configuration POS
  pos_auto_logout_enabled?: boolean;
  pos_auto_logout_delay_seconds?: number;
  pos_default_offline_vendor_id?: string | null;
  pos_virtual_keyboard_enabled?: boolean;
  // Configuration étiquettes Zebra
  zebra_connection_type?: 'USB' | 'NETWORK' | 'BROWSER';
  zebra_usb_queue_name?: string;
  zebra_printer_ip?: string;
  zebra_printer_port?: number;
  label_dpi?: number;
  label_width_mm?: number;
  label_height_mm?: number;
}

interface TenantContextValue {
  tenant: Tenant | null;
  loading: boolean;
  error: string | null;
  setTenant: (tenant: Tenant | null) => void;
  reloadTenant: () => Promise<void>;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

interface TenantProviderProps {
  children: ReactNode;
  initialTenant?: Tenant | null;
}

export function TenantProvider({ children, initialTenant }: TenantProviderProps) {
  const [tenant, setTenantState] = useState<Tenant | null>(initialTenant || null);
  const [loading, setLoading] = useState(!initialTenant);
  const [error, setError] = useState<string | null>(null);

  // Wrapper pour setTenant qui sauvegarde aussi dans localStorage et met à jour l'API client
  const setTenant = async (newTenant: Tenant | null) => {
    setTenantState(newTenant);
    if (newTenant) {
      localStorage.setItem('tenant', JSON.stringify(newTenant));
      logger.log('[TenantContext] Saved tenant to localStorage:', newTenant.id);

      // Mettre à jour l'API client avec le nouveau tenant ID
      const { api } = await import('../services/api');
      api.setTenantId(newTenant.id);
    } else {
      localStorage.removeItem('tenant');

      // Réinitialiser l'API client
      const { api } = await import('../services/api');
      api.setTenantId(null);
    }
  };

  const reloadTenant = async () => {
    if (!tenant?.id) return;

    setLoading(true);
    setError(null);

    try {
      // Import dynamique pour éviter les dépendances circulaires
      const { api } = await import('../services/api');
      const data = await api.getTenant(tenant.id);

      // IMPORTANT: Mettre à jour le localStorage pour que le reload fonctionne
      localStorage.setItem('tenant', JSON.stringify(data));
      logger.log('[TenantContext] Tenant reloaded and cached:', data.id);

      setTenant(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load tenant';
      setError(message);
      logger.error('Failed to reload tenant:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load tenant on mount (si pas d'initialTenant)
  useEffect(() => {
    if (initialTenant) {
      setLoading(false);
      setTenant(initialTenant);
      return;
    }

    // Charger le tenant par défaut
    const loadTenant = async () => {
      // Déclaré hors du try pour être lisible dans le catch (décision offline)
      let usedCache = false;
      try {
        const defaultSlug = 'folies';

        // Essayer d'abord de charger depuis le cache localStorage
        const cachedTenant = localStorage.getItem('tenant');
        if (cachedTenant) {
          try {
            const parsed = JSON.parse(cachedTenant);
            logger.log('[TenantContext] ✅ Loaded tenant from cache:', parsed.id, parsed.name);
            setTenant(parsed);
            setLoading(false);
            usedCache = true;
          } catch (e) {
            logger.warn('[TenantContext] ⚠️ Failed to parse cached tenant, loading from API');
            localStorage.removeItem('tenant');
          }
        }

        // Toujours charger depuis l'API (en arrière-plan si cache utilisé)
        logger.log('[TenantContext] 🌐 Loading tenant from API:', defaultSlug);
        const { api } = await import('../services/api');
        const data = await api.getTenantBySlug(defaultSlug);
        logger.log('[TenantContext] ✅ Loaded tenant from API:', data.id, data.name);

        // Mettre à jour si les données API diffèrent du cache
        const cachedStr = usedCache ? cachedTenant : null;
        const freshStr = JSON.stringify(data);
        if (cachedStr !== freshStr) {
          if (usedCache) {
            logger.log('[TenantContext] 🔄 Cache stale, updating with fresh API data');
          }
          localStorage.setItem('tenant', freshStr);
          setTenant(data);
        }
      } catch (err) {
        // Si le cache a servi, le tenant est déjà dispo : l'échec du refresh API
        // (hors-ligne) est bénin, on ne remonte pas d'erreur bloquante.
        if (usedCache) {
          logger.warn('[TenantContext] Refresh tenant impossible (réseau) — cache conservé (hors-ligne)');
        } else {
          const message = err instanceof Error ? err.message : 'Failed to load default tenant';
          setError(message);
          logger.error('Failed to load tenant:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    loadTenant();
  }, [initialTenant]);

  return (
    <TenantContext.Provider
      value={{
        tenant,
        loading,
        error,
        setTenant,
        reloadTenant,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

/**
 * Hook pour accéder au tenant et sa configuration
 */
export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

/**
 * Hook pour accéder à la configuration du tenant
 * Retourne un objet vide si pas de tenant chargé
 */
export function useTenantConfig(): TenantConfig {
  const { tenant } = useTenant();
  return tenant?.config || {};
}

/**
 * Hook pour vérifier si une feature est activée
 *
 * @example
 * const hasConsignes = useFeature('consignes');
 * const hasWallet = useFeature('wallet');
 */
export function useFeature(featureName: keyof NonNullable<TenantConfig['features']>): boolean {
  const config = useTenantConfig();
  return config.features?.[featureName] ?? false;
}

/**
 * Hook pour accéder aux paramètres hardware
 */
export function useHardwareConfig() {
  const config = useTenantConfig();
  return config.hardware || {};
}

/**
 * Hook pour accéder aux paramètres UI
 */
export function useUIConfig() {
  const config = useTenantConfig();
  return config.ui || {};
}
